import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  GridLegacy as Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ShoppingBag as ShopIcon,
  AttachMoney as MoneyIcon,
  Check as CheckIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { teamService } from '../../services/teamService';
import { avatarService } from '../../services/avatarService';
import { pointsService } from '../../services/pointsService';
import { AvatarItem } from '../../types';

const AvatarShopPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [teamId, setTeamId] = useState('');
  const [shopItems, setShopItems] = useState<(AvatarItem & { owned: boolean })[]>([]);
  const [walletPoints, setWalletPoints] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AvatarItem | null>(null);
  const [purchasing, setPurchasing] = useState(false);

  const categories = [
    { value: 'all', label: 'All Items' },
    { value: 'hat', label: 'Hats' },
    { value: 'shirt', label: 'Shirts' },
    { value: 'pants', label: 'Pants' },
    { value: 'shoes', label: 'Shoes' },
    { value: 'accessory', label: 'Accessories' },
    { value: 'background', label: 'Backgrounds' },
  ];

  const rarities = {
    common: { color: '#8B8B8B', label: 'Common' },
    rare: { color: '#4A90E2', label: 'Rare' },
    epic: { color: '#9C27B0', label: 'Epic' },
    legendary: { color: '#FF9800', label: 'Legendary' },
  };

  useEffect(() => {
    if (user) {
      loadShopData();
    }
  }, [user]);

  const loadShopData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError('');

      // Get user's team
      let userTeam = null;
      if (user.role === 'coach' || user.role === 'headCoach') {
        const coachTeams = await teamService.getTeamsByCoach(user.id);
        userTeam = coachTeams[0] || null;
      } else {
        const athleteTeams = await teamService.getTeamsByAthlete(user.id);
        userTeam = athleteTeams[0] || null;
      }
      
      if (!userTeam) {
        setError('You are not a member of any team.');
        return;
      }

      setTeamId(userTeam.id);

      // Load shop items with ownership status
      const items = await avatarService.getShopItemsWithOwnership(user.id, userTeam.id);
      setShopItems(items);

      // Load user's wallet points
      const pointsSummary = await pointsService.getPointsSummary(user.id, userTeam.id);
      setWalletPoints(pointsSummary.walletPoints || 0);

    } catch (err) {
      console.error('Failed to load shop data:', err);
      setError('Failed to load shop. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseClick = (item: AvatarItem) => {
    if (item.price > walletPoints) {
      setError(`You need ${item.price - walletPoints} more wallet points to purchase this item.`);
      return;
    }
    setSelectedItem(item);
    setPurchaseDialogOpen(true);
  };

  const handlePurchase = async () => {
    if (!selectedItem || !user || !teamId) return;

    try {
      setPurchasing(true);
      setError('');
      setSuccess('');

      const result = await avatarService.purchaseAvatarItem(
        user.id,
        teamId,
        selectedItem.id,
        walletPoints
      );

      if (result.success) {
        setSuccess(`Successfully purchased ${selectedItem.name}!`);
        setPurchaseDialogOpen(false);
        setSelectedItem(null);
        
        // Reload shop data to update ownership and wallet points
        await loadShopData();
      } else {
        setError(result.error || 'Purchase failed. Please try again.');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setError('Purchase failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const filteredItems = selectedCategory === 'all' 
    ? shopItems 
    : shopItems.filter(item => item.category === selectedCategory);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShopIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Avatar Shop
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Purchase items for your avatar using wallet points
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon sx={{ color: 'success.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
            {walletPoints} WP
          </Typography>
        </Box>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Category Filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((category) => (
              <MenuItem key={category.value} value={category.value}>
                {category.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Shop Items Grid */}
      <Grid container spacing={3}>
        {filteredItems.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ShopIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No items available in this category
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back later or ask your coach to add more items!
              </Typography>
            </Box>
          </Grid>
        ) : (
          filteredItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: item.owned ? 0.7 : 1,
                  border: item.owned ? '2px solid #4CAF50' : '1px solid #e0e0e0'
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={item.imageUrl}
                  alt={item.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      {item.name}
                    </Typography>
                    {item.owned && (
                      <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                    {item.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={rarities[item.rarity].label}
                      size="small"
                      sx={{ 
                        backgroundColor: rarities[item.rarity].color,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        {item.price} WP
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button
                    variant={item.owned ? "outlined" : "contained"}
                    fullWidth
                    disabled={item.owned || item.price > walletPoints}
                    onClick={() => handlePurchaseClick(item)}
                    startIcon={item.owned ? <CheckIcon /> : <MoneyIcon />}
                  >
                    {item.owned ? 'Owned' : 
                     item.price > walletPoints ? 'Insufficient WP' : 'Purchase'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Purchase Confirmation Dialog */}
      <Dialog 
        open={purchaseDialogOpen} 
        onClose={() => setPurchaseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Purchase</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to purchase <strong>{selectedItem.name}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedItem.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">
                  Cost: <strong>{selectedItem.price} Wallet Points</strong>
                </Typography>
                <Typography variant="body2">
                  Remaining: <strong>{walletPoints - selectedItem.price} Wallet Points</strong>
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseDialogOpen(false)} disabled={purchasing}>
            Cancel
          </Button>
          <Button 
            onClick={handlePurchase} 
            variant="contained" 
            disabled={purchasing}
            startIcon={purchasing ? <CircularProgress size={16} /> : <MoneyIcon />}
          >
            {purchasing ? 'Purchasing...' : 'Purchase'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AvatarShopPage;
